import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Home, Calendar, Users, Sparkles, Menu } from "lucide-react";
import BackToMore from "./BackToMore";

// Top-bar titles match Pre K headerTitle values
const TITLES = {
  "/": "Nurture. Guide. Empower.",
  "/agenda": "Today's Agenda",
  "/group": "Group Rotation",
  "/sessions": "Sessions",
  "/more": "More",
  "/locations": "Locations",
  "/resources": "Resources",
  "/feedback": "Feedback",
  "/admin": "Admin",
  "/success-starts-here": "Success Starts Here",
  "/keynote": "Keynote Speaker",
  "/ai-games": "AI Online Learning Games",
};

const navItems = [
  { to: "/", icon: Home, label: "Home", testid: "nav-home" },
  { to: "/agenda", icon: Calendar, label: "Agenda", testid: "nav-agenda" },
  { to: "/group", icon: Users, label: "My Group", testid: "nav-group" },
  { to: "/sessions", icon: Sparkles, label: "Sessions", testid: "nav-sessions" },
  { to: "/more", icon: Menu, label: "More", testid: "nav-more" },
];

const moreSubRoutes = [
  "/locations",
  "/resources",
  "/ai-games",
  "/feedback",
  "/admin",
  "/success-starts-here",
  "/keynote",
];

const isAdminRoute = (p) => p === "/admin";

function isNavItemActive(to, pathname) {
  if (to === "/") return pathname === "/";
  if (to === "/more") {
    return pathname === "/more" || moreSubRoutes.includes(pathname);
  }
  return pathname === to || pathname.startsWith(to + "/");
}

export default function Layout() {
  const { pathname } = useLocation();

  let title = TITLES[pathname] || "";
  if (!title && pathname.startsWith("/group/")) title = "Group Rotation";

  // Admin uses primaryDark (#092936), all others use primary (#145261)
  const headerBg = isAdminRoute(pathname) ? "var(--primary-dark)" : "var(--primary)";

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: "var(--background)" }}
      data-testid="layout-root"
    >
      {/* Top header bar — solid teal, white title, weight 700 size 18 */}
      <header
        className="sticky top-0 z-40"
        style={{ background: headerBg, color: "white" }}
        data-testid="top-header"
      >
        <div className="px-4 py-3 min-h-[48px] flex items-center">
          <h1
            className="text-[18px] font-bold tracking-tight"
            data-testid="page-title"
          >
            {title}
          </h1>
        </div>
      </header>

      {/* Page content — full width on mobile */}
      <main
        className="relative z-10 pb-[80px]"
        data-testid="main-content"
      >
        {moreSubRoutes.includes(pathname) && <BackToMore />}
        <Outlet />
      </main>

      {/* Bottom tab bar — white, primary active, gray inactive, with icons */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40"
        style={{
          background: "var(--surface)",
          borderTop: "1px solid var(--border)",
          height: "64px",
          paddingTop: "6px",
          paddingBottom: "8px",
        }}
        data-testid="bottom-nav"
      >
        <div className="h-full grid grid-cols-5">
          {navItems.map((it) => {
            const Icon = it.icon;
            const isActive = isNavItemActive(it.to, pathname);
            const color = isActive ? "var(--primary)" : "var(--tab-inactive)";
            return (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className="flex flex-col items-center justify-center gap-0.5"
                style={{ color }}
              >
                <Icon size={22} strokeWidth={1.9} />
                <span
                  className="text-[11px] font-semibold tracking-wide"
                  style={{ color }}
                >
                  {it.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
